import { cn } from '@/lib/utils';
import { Button } from '@ui/button';
import { Card, CardContent } from '@ui/authcard';
import { Input } from '@ui/input';
import { Label } from '@ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@ui/tooltip';
import { ChevronDownIcon, Eye, EyeOff } from 'lucide-react';
import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowTurnBackwardIcon } from '@hugeicons/core-free-icons';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import {
  validateMyanmarPhoneNumber,
  formatPhoneNumberDisplay,
  toApiFormat,
} from '../../utils/phoneValidation';

interface SignupFormProps extends React.ComponentProps<'div'> {
  onSuccess?: () => void;
  onStepChange?: (step: number) => void;
}

export function SignupForm({ className, onSuccess, onStepChange, ...props }: SignupFormProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    otp: '',
    password: '',
    verifyPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [countdown, setCountdown] = useState(15 * 60); // 15 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [showPhoneValidation, setShowPhoneValidation] = useState(false);
  const { register, verifyOTP, sendOTP } = useAuth();

  // Notify parent component when step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);
  const { showAuthDialog } = useAuthDialog();
  const { showToast } = useToast();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 3 && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const formattedValue = formatPhoneNumberDisplay(value);

      // Validate the phone number
      if (value.trim()) {
        const validation = validateMyanmarPhoneNumber(value);
        if (!validation.isValid) {
          setPhoneValidationError(validation.error || 'Invalid phone number');
        } else {
          setPhoneValidationError('');
        }
      } else {
        setPhoneValidationError('');
      }

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔥 handleSubmit called');
    console.log('📋 Current formData:', formData);
    console.log('📍 Current step:', step);

    // Validate phone number
    if (!formData.phoneNumber.trim()) {
      console.log('❌ Phone number is empty');
      setShowPhoneValidation(true);
      setTimeout(() => setShowPhoneValidation(false), 3000);
      return;
    }

    console.log('📞 Validating phone number:', formData.phoneNumber);
    const validation = validateMyanmarPhoneNumber(formData.phoneNumber);
    console.log('✅ Phone validation result:', validation);

    if (!validation.isValid) {
      console.log('❌ Phone validation failed:', validation.error);
      setPhoneValidationError(validation.error || 'Invalid phone number');
      return;
    }

    console.log('🚀 Calling handleNextStep');
    handleNextStep(e);
  };

  const handleNextStep = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔥 handleNextStep called');
    console.log('📋 Current formData in handleNextStep:', formData);
    console.log('📍 Current step in handleNextStep:', step);

    setIsLoading(true);
    setError('');

    try {
      if (step === 1 && formData.fullName && formData.email && formData.phoneNumber) {
        console.log('✅ Step 1 validation passed - moving to step 2');
        console.log('👤 Full Name:', formData.fullName);
        console.log('📧 Email:', formData.email);
        console.log('📞 Phone:', formData.phoneNumber);
        // Move to password setup step
        setStep(2);
      } else if (step === 2 && formData.password && formData.verifyPassword) {
        // Complete registration
        if (formData.password !== formData.verifyPassword) {
          setError('Passwords do not match.');
          return;
        }

        const apiPhoneNumber = toApiFormat(formData.phoneNumber);
        const response = await register({
          phoneNumber: apiPhoneNumber,
          email: formData.email,
          password: formData.password,
          name: formData.fullName,
        });

        if (response.success) {
          // Registration successful, move to OTP verification
          if (response.message) {
            showToast('success', response.message);
          }
          setStep(3);
        } else {
          if (response.error) {
            setError(response.error);
            showToast('error', response.error);
          }
        }
      } else if (step === 3 && formData.otp) {
        // Verify OTP
        // const cleanPhoneNumber = formData.phoneNumber.replace(/\s/g, '');
        const response = await verifyOTP({
          email: formData.email,
          otp: formData.otp,
        });

        if (response.success) {
          // OTP verified, call success callback or show auth dialog
          if (response.message) {
            showToast('success', response.message);
          }
          if (onSuccess) {
            onSuccess();
          } else {
            showAuthDialog('login');
          }
        } else {
          if (response.error) {
            setError(response.error);
            showToast('error', response.error);
          }
        }
      }
    } catch (error) {
      logger.error('Signup error:', error);
      // Only show toast if error has a message property
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setError(error.message);
        showToast('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleResendOTP = async () => {
    try {
      // const cleanPhoneNumber = formData.phoneNumber.replace(/\s/g, '');
      const response = await sendOTP({ email: formData.email });

      if (response.success) {
        setCountdown(15 * 60);
        setCanResend(false);
        setError('');
        showToast('success', response.message || t('messages.otp_sent_successfully'));
        // Close the dialog after successful OTP sending
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.error || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      logger.error('Resend OTP error:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Format seconds to mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            {/* Step 1: Personal Information */}
            <div className="grid gap-2">
              <Label htmlFor="fullName">{t('auth.full_name')}</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder={t('placeholders.full_name')}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="name@example.com"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone">{t('auth.phone')}</Label>
              <div className="flex rounded-md relative">
                <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                  <select
                    id="country"
                    name="country"
                    autoComplete="country"
                    aria-label="Country"
                    disabled
                    defaultValue="+95"
                    className="col-start-1 row-start-1 w-full appearance-none rounded-l-md border border-input bg-background py-1.5 pr-7 pl-3 text-sm text-muted-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option>+95</option>
                  </select>
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-muted-foreground"
                  />
                </div>
                <Tooltip open={!!phoneValidationError}>
                  <TooltipTrigger asChild>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="09 xxx xxxxxx"
                      maxLength={13}
                      className="rounded-l-none border-l-0 focus:border-l focus:ring-offset-0"
                      required
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div className="flex items-center gap-2">
                      <span className="text-red-600">⚠</span>
                      <span>{phoneValidationError}</span>
                    </div>
                  </TooltipContent>
                </Tooltip>
                {showPhoneValidation && (
                  <div className="relative">
                    <Tooltip open={showPhoneValidation}>
                      <TooltipTrigger asChild>
                        <div className="absolute inset-0 pointer-events-none" />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-600 font-bold">!</span>
                          <span>Please fill out this field.</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              Continue
            </Button>
          </>
        );
      case 2:
        return (
          <>
            {/* Step 2: Password Setup */}
            <div className="grid gap-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="verifyPassword">{t('auth.verify_password')}</Label>
              <div className="relative">
                <Input
                  id="verifyPassword"
                  name="verifyPassword"
                  type={showVerifyPassword ? 'text' : 'password'}
                  value={formData.verifyPassword}
                  onChange={handleInputChange}
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                >
                  {showVerifyPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full group relative overflow-hidden"
                onClick={handlePrevStep}
              >
                <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={24} className="relative z-10" />
              </Button>
              <Button type="submit" className="w-full col-span-5" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </>
        );
      case 3:
        return (
          <>
            {/* Step 3: OTP Verification */}
            <div className="grid text-center gap-4">
              <p className="text-sm text-muted-foreground">
                We've sent a verification code to {formData.email}
              </p>
              <div>
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <Input
                      key={i}
                      type="text"
                      maxLength={1}
                      className="aspect-square text-center text-lg"
                      name={`otp-${i}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length === 1 && i < 5) {
                          const nextInput = document.querySelector(`input[name=otp-${i + 1}]`);
                          if (nextInput) {
                            (nextInput as HTMLInputElement).focus();
                          }
                        }
                        setFormData((prev) => ({
                          ...prev,
                          otp: prev.otp.slice(0, i) + value + prev.otp.slice(i + 1),
                        }));
                      }}
                      required
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center text-center text-sm">
                {canResend ? (
                  <Button onClick={handleResendOTP} variant="link" className="p-0 h-auto">
                    Resend OTP
                  </Button>
                ) : (
                  <div className="text-muted-foreground cursor-not-allowed text-center">
                    <span className="animate-pulse">Resend OTP in </span>
                    {formatTime(countdown)}
                    {/* Valid until {formatTime(countdown)} */}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-6 gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full group relative overflow-hidden"
                  onClick={handlePrevStep}
                >
                  <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={24} className="relative z-10" />
                </Button>
                <Button type="submit" className="w-full col-span-5" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden py-0">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {getStepContent()}
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our <a href="#">Terms of Service</a> and{' '}
        <a href="/privacy-policy">Privacy Policy</a>.
      </div>
    </div>
  );
}
